
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Filter } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DiscoverySubCategoriesView from "./DiscoverySubCategoriesView";
import DiscoveryFilters from "./DiscoveryFilters";
import ProfileGrid from "./ProfileGrid";
import ProfileModal from "./ProfileModal";
import SubscriptionBanner from "./SubscriptionBanner";
import SubscriptionLimitModal from "./SubscriptionLimitModal";
import { toast } from "@/hooks/use-toast";
import { DiscoveryRibbonType } from "./DiscoveryRibbons";

interface DiscoveryContentProps {
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  hasActiveFilters: boolean;
  filters: any;
  setFilters: (filters: any) => void;
  hasHitLimit: boolean;
  upgradeMessage: string;
  showLimitModal: boolean;
  setShowLimitModal: (show: boolean) => void;
  currentTier: string;
  profileLimit: number;
  nearbyUsers: any[];
  isLoading: boolean;
  onRefresh: () => void;
  onProfileView: (userId: string, userProfile?: any) => void;
  hasLocation: boolean;
  locationPermission: string;
  activeRibbon: DiscoveryRibbonType;
  onRibbonChange: (ribbon: DiscoveryRibbonType) => void;
}

const DiscoveryContent = ({
  showFilters,
  setShowFilters,
  hasActiveFilters,
  filters,
  setFilters,
  hasHitLimit,
  upgradeMessage,
  showLimitModal,
  setShowLimitModal,
  currentTier,
  profileLimit,
  nearbyUsers,
  isLoading,
  onRefresh,
  onProfileView,
  hasLocation,
  locationPermission,
  activeRibbon,
  onRibbonChange
}: DiscoveryContentProps) => {
  const navigate = useNavigate();
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [profilesViewed, setProfilesViewed] = useState(0);

  const handleUpgradeClick = () => {
    navigate('/settings?tab=subscription');
  };

  const handleProfileClick = (profile: any) => {
    console.log('🔍 Profile clicked in DiscoveryContent:', profile);
    
    try {
      setSelectedProfile(profile);
      setProfilesViewed(prev => prev + 1);
      onProfileView(profile.user_id, profile);
    } catch (error) {
      console.error('❌ Error handling profile click:', error);
      toast({
        title: "Navigation Error",
        description: "Failed to view profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleProfileView = (userId: string, userProfile?: any) => {
    console.log('🔍 Profile view requested:', { userId, userProfile });
    
    try {
      setProfilesViewed(prev => prev + 1);
      onProfileView(userId, userProfile);
    } catch (error) {
      console.error('❌ Error in profile view:', error);
      toast({
        title: "Navigation Error",
        description: "Failed to navigate to profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleTabChange = (value: string) => {
    console.log('🔄 Tab changed to:', value, 'from:', activeRibbon);
    onRibbonChange(value as DiscoveryRibbonType);
  };

  return (
    <div className="w-full">
      <Tabs value={activeRibbon} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-4 bg-card/80 dark:bg-carbon/50 border border-border dark:border-ice-white/20 mx-0 py-0 px-[14px] my-0" data-testid="discovery-tabs">
          <TabsTrigger 
            value="discovery" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-muted-foreground dark:text-ice-white/70 font-medium my-[6px] py-0"
            data-testid="discovery-tab-discovery"
          >
            Discovery
          </TabsTrigger>
          <TabsTrigger 
            value="nearby" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-muted-foreground dark:text-ice-white/70 font-medium py-0"
          >
            Nearby
          </TabsTrigger>
          <TabsTrigger 
            value="activity" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-muted-foreground dark:text-ice-white/70 font-medium py-0"
          >
            Activity
          </TabsTrigger>
          <TabsTrigger 
            value="buzzz" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-muted-foreground dark:text-ice-white/70 font-medium py-0"
          >
            Buzzz
          </TabsTrigger>
        </TabsList>

        <TabsContent value="discovery" className="mt-0">
          <DiscoverySubCategoriesView onProfileView={handleProfileView} />
        </TabsContent>

        <TabsContent value="nearby" className="mt-0">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <h2 className="text-2xl font-bold text-foreground dark:text-ice-white mb-2">
                  Nearby Explorers
                </h2>
                <p className="text-muted-foreground dark:text-ice-white/60 text-sm">
                  Discover profiles sorted by distance
                </p>
              </div>
              <button
                onClick={() => setShowFilters(true)}
                className={`p-2 rounded-lg transition-colors ${
                  hasActiveFilters 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-card/80 dark:bg-carbon/50 border border-border dark:border-ice-white/20 text-muted-foreground dark:text-ice-white/70 hover:text-foreground dark:hover:text-ice-white'
                }`}
                data-testid="filter-button"
              >
                <Filter className="w-5 h-5" />
                {hasActiveFilters && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-neon-red rounded-full"></span>
                )}
              </button>
            </div>
            <div data-testid="profile-grid">
              <ProfileGrid
                users={nearbyUsers}
                isLoading={isLoading}
                onRefresh={onRefresh}
                onProfileView={handleProfileView}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mt-0">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <h2 className="text-2xl font-bold text-foreground dark:text-ice-white mb-2">
                  Active Now
                </h2>
                <p className="text-muted-foreground dark:text-ice-white/60 text-sm">
                  Recently active users
                </p>
              </div>
              <button
                onClick={() => setShowFilters(true)}
                className={`p-2 rounded-lg transition-colors ${
                  hasActiveFilters 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-card/80 dark:bg-carbon/50 border border-border dark:border-ice-white/20 text-muted-foreground dark:text-ice-white/70 hover:text-foreground dark:hover:text-ice-white'
                }`}
              >
                <Filter className="w-5 h-5" />
                {hasActiveFilters && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-neon-red rounded-full"></span>
                )}
              </button>
            </div>
            <ProfileGrid
              users={nearbyUsers}
              isLoading={isLoading}
              onRefresh={onRefresh}
              onProfileView={handleProfileView}
            />
          </div>
        </TabsContent>

        <TabsContent value="buzzz" className="mt-0">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <h2 className="text-2xl font-bold text-foreground dark:text-ice-white mb-2">
                  Most Buzzz'd
                </h2>
                <p className="text-muted-foreground dark:text-ice-white/60 text-sm">
                  Users sorted by Buzzz count
                </p>
              </div>
              <button
                onClick={() => setShowFilters(true)}
                className={`p-2 rounded-lg transition-colors ${
                  hasActiveFilters 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-card/80 dark:bg-carbon/50 border border-border dark:border-ice-white/20 text-muted-foreground dark:text-ice-white/70 hover:text-foreground dark:hover:text-ice-white'
                }`}
              >
                <Filter className="w-5 h-5" />
                {hasActiveFilters && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-neon-red rounded-full"></span>
                )}
              </button>
            </div>
            <ProfileGrid
              users={nearbyUsers}
              isLoading={isLoading}
              onRefresh={onRefresh}
              onProfileView={handleProfileView}
            />
          </div>
        </TabsContent>
      </Tabs>

      {hasHitLimit && (
        <div className="mb-6">
          <div className="bg-card/80 dark:bg-carbon/50 border border-primary/30 dark:border-neon-red/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground dark:text-ice-white mb-2">
                  Upgrade to Continue Exploring
                </h3>
                <p className="text-muted-foreground dark:text-ice-white/70 text-sm">{upgradeMessage}</p>
              </div>
              <button
                onClick={handleUpgradeClick}
                className="bg-primary hover:bg-primary/80 dark:bg-neon-red dark:hover:bg-neon-red/80 text-primary-foreground dark:text-ice-white text-sm px-6 py-2 rounded-lg transition-colors"
              >
                Upgrade
              </button>
            </div>
          </div>
        </div>
      )}

      {showFilters && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-card dark:bg-carbon border border-border dark:border-ice-white/20 rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
            <DiscoveryFilters
              filters={filters}
              onFiltersChange={setFilters}
              onClose={() => setShowFilters(false)}
            />
          </div>
        </div>
      )}

      {selectedProfile && (
        <ProfileModal
          user={selectedProfile}
          isOpen={!!selectedProfile}
          onClose={() => setSelectedProfile(null)}
        />
      )}

      {showLimitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-card dark:bg-carbon border border-border dark:border-ice-white/20 rounded-lg max-w-md w-full">
            <SubscriptionLimitModal
              isOpen={showLimitModal}
              onClose={() => setShowLimitModal(false)}
              currentTier={currentTier as any}
              profilesViewed={profilesViewed}
              profileLimit={profileLimit}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscoveryContent;
